import React, { useState, useEffect } from 'react';
import { FaWhatsapp, FaTimes, FaFacebook, FaInstagram, FaTiktok } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const WhatsAppButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const phoneNumber = process.env.REACT_APP_WHATSAPP_NUMBER || '923488477649';

  const handleSendMessage = () => {
    if (message.trim()) {
      const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
      setIsOpen(false);
      setMessage('');
    }
  };

  const quickMessages = [
    'I want to know about latest phones',
    'What is the price of iPhone 15 Pro?',
    'Do you have installment plans?',
    'Is warranty available?',
    'Can I visit your shop?',
  ];

  const socialLinks = [
    {
      id: 'tiktok',
      icon: <FaTiktok size={18} />,
      url: 'https://www.tiktok.com/@wazirimobile46?_r=1&_t=ZS-96XiBCx3w1N',
      color: 'bg-black text-white hover:bg-slate-900 shadow-[0_0_10px_rgba(0,242,234,0.3)] hover:shadow-[0_0_15px_rgba(0,242,234,0.5)] border border-slate-800',
      tooltip: 'Follow us on TikTok',
      bottomClass: 'bottom-[204px]'
    },
    {
      id: 'facebook',
      icon: <FaFacebook size={18} />,
      url: 'https://www.facebook.com/share/1HRFYvPFB8/?mibextid=wwXIfr',
      color: 'bg-[#1877F2] text-white hover:bg-[#166fe5] shadow-md hover:shadow-lg',
      tooltip: 'Follow us on Facebook',
      bottomClass: 'bottom-[152px]'
    },
    {
      id: 'instagram',
      icon: <FaInstagram size={18} />,
      url: 'https://www.instagram.com/waziri_mobile_46?igsh=MWdpZ2hlamJwYnQ5NA==',
      color: 'bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] text-white hover:opacity-90 shadow-md hover:shadow-lg',
      tooltip: 'Follow us on Instagram',
      bottomClass: 'bottom-24'
    }
  ];

  if (!show) return null;

  return (
    <>
      {/* WhatsApp Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 bg-green-500 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-green-600 transition-all duration-300 group"
      >
        {isOpen ? <FaTimes size={24} /> : <FaWhatsapp size={28} />}
        <span className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-ping" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-6 z-50 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-green-500 text-white p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <FaWhatsapp className="text-green-500 text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold">WhatsApp Chat</h3>
                  <p className="text-xs text-green-100">Typically replies within 5 minutes</p>
                </div>
              </div>
            </div>

            {/* Quick Messages */}
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs text-gray-500 mb-2">Quick replies:</p>
              <div className="flex flex-wrap gap-2">
                {quickMessages.map((msg, idx) => (
                  <button
                    key={idx}
                    onClick={() => setMessage(msg)}
                    className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    {msg}
                  </button>
                ))}
              </div>
            </div>

            {/* Message Input */}
            <div className="p-4">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                rows="3"
              />
              <button
                onClick={handleSendMessage}
                className="w-full mt-3 bg-green-500 text-white py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors"
              >
                Send Message
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Social Links */}
      <AnimatePresence>
        {!isOpen && socialLinks.map((link, index) => (
          <motion.a
            key={link.id}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ scale: 0, opacity: 0, x: 20 }}
            animate={{ scale: 1, opacity: 1, x: 0 }}
            exit={{ scale: 0, opacity: 0, x: 20 }}
            transition={{
              type: 'spring',
              stiffness: 260,
              damping: 20,
              delay: 0.05 * index
            }}
            whileHover={{ scale: 1.1 }}
            className={`fixed ${link.bottomClass} right-[30px] z-40 ${link.color} w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 group`}
          >
            {link.icon}
            
            {/* Tooltip */}
            <span className="absolute right-14 bg-slate-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-md border border-slate-800">
              {link.tooltip}
            </span>
          </motion.a>
        ))}
      </AnimatePresence>
    </>
  );
};

export default WhatsAppButton;